import React, { ComponentProps, useState } from 'react';
import styled from 'styled-components';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import { Skeleton } from 'primereact/skeleton';
import { useAdminLookup } from 'services/api/queries';
import { FieldPanelHeader, HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { PatchedToolForAdminModel } from 'types/admin-tools/PatchedToolForAdminModel';
import { ToolForAdminModelVersion } from 'types/admin-tools/ToolForAdminModelVersion';
import { AdminLookupKeyEnum } from 'types/types';
import { FormikLikeErrors } from './utils/getErrorsLikeFormik';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import { HelpBox } from 'components/v2/help-boxes';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import FileUpload from 'components/v2/file-upload/FileUpload';
import { ButtonUploadIcon } from 'components/v2/icons';
import { useAddToolVersionFileMutation, useDeleteToolVersionFileMutation } from 'services/api/adminToolsQueriesAndMutations';
import TextWithAnimatedEllipsis from 'components/v2/file-upload/TextWithAnimatedEllipsis';
import FilesList from 'components/v2/file-row/FilesList';

interface Props {
  toolData: PatchedToolForAdminModel;
  versionData: ToolForAdminModelVersion;
  errors: FormikLikeErrors<ToolForAdminModelVersion>;
}

const AdminEditToolVersionFiles: React.FC<Props> = ({ toolData, versionData, errors }) => {
  const fileTypeOptions = useAdminLookup(AdminLookupKeyEnum.TOOL_FILE_TYPES).data;
  const [selectedFileTypeIndex, setSelectedFileTypeIndex] = useState<number>(-1);
  const addToolVersionFileMutation = useAddToolVersionFileMutation(toolData.id, versionData.ord);
  const deleteToolVersionFileMutation = useDeleteToolVersionFileMutation(toolData.id, versionData.ord);

  if (!fileTypeOptions) {
    return <Skeleton height="10rem" />;
  }

  const handleUploadFile: ComponentProps<typeof FileUpload>['onFileUpload'] = (ev) => {
    const file = ev.target.files?.[0];
    if (!file || selectedFileTypeIndex === -1) {
      return;
    }

    const fileType = fileTypeOptions[selectedFileTypeIndex];
    if (!fileType || !fileType.value) {
      return;
    }

    setSelectedFileTypeIndex(-1);
    addToolVersionFileMutation.mutate({
      fileTypeId: fileType.value,
      fileData: file,
    });
  };

  const prettyFileTypes = fileTypeOptions.map((o) => o.label).join(', ');
  const helpText = `Please upload Tool related documents (${prettyFileTypes}) in DOCX or PDF format. Several documents can be uploaded (minimum 3 files).`;

  return (
    <Wrapper className="w-full">
      <FieldPanelHeader>
        <HeaderCaptionSemiBold>Tool related documents</HeaderCaptionSemiBold>
        <TooltipHelpIcon content={helpText} />
        {errors.files && <TooltipErrorIcon content={errors.files} />}
      </FieldPanelHeader>

      <HelpBox>{helpText}</HelpBox>

      <div>
        <CompilerSelectControlledComponent
          label="Document type"
          name="fileType"
          options={fileTypeOptions}
          value={selectedFileTypeIndex === -1 ? null : fileTypeOptions[selectedFileTypeIndex]}
          onChanged={(_, fileTypeId) => {
            setSelectedFileTypeIndex(fileTypeOptions.findIndex((o) => o.value === fileTypeId));
          }}
          placeholder="Select"
          isClearable
        />
        <FileUpload
          label="Upload document"
          name="file"
          icon={ButtonUploadIcon}
          accept=".docx,.pdf"
          validExtensions={['docx', 'pdf']}
          onFileUpload={handleUploadFile}
          disabled={selectedFileTypeIndex === -1 || addToolVersionFileMutation.isPending}
        />
      </div>

      {addToolVersionFileMutation.isPending && <TextWithAnimatedEllipsis>Uploading file</TextWithAnimatedEllipsis>}

      <FilesList files={versionData.files} onRemoveFile={(fileId) => deleteToolVersionFileMutation.mutate(fileId)} />
    </Wrapper>
  );
};

export default AdminEditToolVersionFiles;

const Wrapper = styled.div`
  display: grid;
  gap: 16px;
`;
